import React, { useState, useEffect, useMemo } from "react";
import { useNavigation } from "@react-navigation/native";
import { StyleSheet, View, ScrollView } from "react-native";
import { IError } from "src/components/dialog/error";
import { Container, Input, Button, Typography } from "src/components";
import { useForm } from "react-hook-form";
import * as formValidators from "src/utils/form-validator";
import { Auth } from "aws-amplify";
import { colors } from "src/assets";
import { useCognito } from "src/hooks";
import LocalText from "src/local";
import { RootStackScreenProps } from "src/navigation/types";

type FormValues = {
  oldPassword: string;
  password: string;
  passwordConfirm: string;
};

export const MyAccountPasswordScreen: React.FC = ({}) => {
  const navigation =
    useNavigation<RootStackScreenProps<"MyAccountPassword">["navigation"]>();
  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { oldPassword: "", password: "", passwordConfirm: "" },
  });
  const [error, setError] = useState<IError | null>(null);
  const [processed, setProcessed] = useState<boolean>(false);

  useEffect(() => {
    register("oldPassword", {
      validate: (value) => {
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.myAccountPassword
            .oldPasswordValidationRequired;
        }
        // TODO: Add password pattern
        return true;
      },
    });
    register("password", {
      validate: (value) => {
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.myAccountPassword.passwordValidationRequired;
        }
        // TODO: Add password pattern
        return true;
      },
    });
    register("passwordConfirm", {
      validate: (value) => {
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.myAccountPassword
            .passwordConfirmValidationRequired;
        }
        if (getValues("password") !== value) {
          return LocalText.screens.myAccountPassword
            .passwordConfirmValidationMismatch;
        }
        // TODO: Add password pattern
        return true;
      },
    });
  }, [register]);
  const onConfirm = async (data: FormValues) => {
    setProcessed(true);
    try {
      const cognitoUser = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(cognitoUser, data.oldPassword, data.password);
      setProcessed(false);
      navigation.goBack();
    } catch (e) {
      console.error(e);
      setError(e as Error);
      setProcessed(false);
    }
  };
  const input1ErrorMessage = useMemo(() => {
    if (error?.code === "NotAuthorizedException") {
      return LocalText.error.amplifyAuthInvalidPasswordException;
    }
    return undefined;
  }, [error]);
  const input2ErrorMessage = useMemo(() => {
    if (error?.code === "NotAuthorizedException") {
      return undefined;
    } else if (error?.code === "InvalidPasswordException") {
      return LocalText.error.amplifyAuthInvalidPasswordException;
    }
    return error?.code;
  }, [error]);
  return (
    <Container style={styles.container} loading={processed}>
      <ScrollView style={styles.scrollView}>
        <Typography
          text={LocalText.screens.myAccountPassword.password}
          variant={"section"}
        />
        <Input
          placeholder={LocalText.screens.myAccountPassword.oldPassword}
          name={"oldPassword"}
          control={control}
          error={errors.oldPassword?.message || input1ErrorMessage}
          secureTextEntry={true}
        />
        <Input
          placeholder={LocalText.screens.myAccountPassword.newPassword}
          name={"password"}
          control={control}
          error={errors.password?.message || input2ErrorMessage}
          secureTextEntry={true}
        />
        <Input
          placeholder={LocalText.screens.myAccountPassword.newPasswordConfirm}
          name={"passwordConfirm"}
          control={control}
          error={errors.passwordConfirm?.message}
          secureTextEntry={true}
        />
        <View style={styles.buttonContainer}>
          <Button
            title={LocalText.common.done}
            loading={processed}
            onPress={handleSubmit(onConfirm)}
          />
        </View>
      </ScrollView>
    </Container>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.gray1,
  },
  scrollView: {
    flex: 1,
  },
  buttonContainer: {
    flexDirection: "column",
    marginTop: 36,
    marginHorizontal: 16,
  },
});
